import React, { useState, useEffect } from 'react';
import JobSearchSelect from '../EvaluationComponents/Shards/CreateEvaluation/JobSearchSelect';
import TalentSearchSelect from '../EvaluationComponents/Shards/CreateEvaluation/TalentSearchSelect';
import RoleSearchSelect from '../EvaluationComponents/Shards/CreateEvaluation/RoleSearchSelect';

const Step_Start = ({ data, onDataChange, evaluationData }) => {
  // Generate default evaluation name with current date and time
  const getDefaultEvaluationName = () => {
    const now = new Date();
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                   'July', 'August', 'September', 'October', 'November', 'December'];
    const month = months[now.getMonth()];
    const day = now.getDate();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');
    
    return `Evaluation ${month} ${day}, ${hours}:${minutes}:${seconds}`;
  };

  // Local state for form data
  const [formData, setFormData] = useState({
    evaluationType: 'Job',
    selectedJob: null,
    selectedTalent: null,
    selectedRole: null,
    seniority: 'Senior',
    evaluation_name: getDefaultEvaluationName(),
    is_default_evaluation: false,
    countdown_enabled: false,
    evaluationSteps: {
      resume: true,
      kyc: false,
      video_intro: false,
      questionnaire: true,
      challenge: true
    },
    ...data // Override with any passed data
  });

  // State for validation errors
  const [validationErrors, setValidationErrors] = useState({});
  const [showValidation, setShowValidation] = useState(false);

  // Send initial data to parent on component mount
  useEffect(() => {
    onDataChange?.(formData);
  }, []); // Empty dependency array means this runs once on mount

  // Handle form changes and notify parent
  const handleChange = (field, value) => {
    let newData = { ...formData, [field]: value };
    
    // Reset job/talent/role data when switching evaluation types
    if (field === 'evaluationType') {
      newData = {
        ...newData,
        selectedJob: null,
        selectedTalent: null,
        selectedRole: null,
        seniority: 'Senior'
      };
    }
    
    setFormData(newData);
    onDataChange?.(newData);
  };

  // Handle job selection
  const handleJobSelect = (job) => {
    console.log('Selected job object:', job);
    const newData = { ...formData, selectedJob: job };
    setFormData(newData);
    onDataChange?.(newData);
  };

  // Handle talent selection
  const handleTalentSelect = (talent) => {
    console.log('Selected talent object:', talent);
    const newData = { ...formData, selectedTalent: talent };
    setFormData(newData);
    onDataChange?.(newData);
  };

  // Clear job selection
  const clearJobSelection = () => {
    const newData = { ...formData, selectedJob: null };
    setFormData(newData);
    onDataChange?.(newData);
  };

  // Clear talent selection
  const clearTalentSelection = () => {
    const newData = { ...formData, selectedTalent: null };
    setFormData(newData);
    onDataChange?.(newData);
  };

  // Handle role selection
  const handleRoleSelect = (role) => {
    const newData = { ...formData, selectedRole: role };
    setFormData(newData);
    onDataChange?.(newData);
  };

  // Clear role selection
  const clearRoleSelection = () => {
    const newData = { ...formData, selectedRole: null };
    setFormData(newData);
    onDataChange?.(newData);
  };

  // Handle seniority change
  const handleSeniorityChange = (e) => {
    const newData = { ...formData, seniority: e.target.value };
    setFormData(newData);
    onDataChange?.(newData);
  };

  // Handle evaluation steps checkbox changes
  const handleStepChange = (stepKey, checked) => {
    const newData = {
      ...formData,
      evaluationSteps: {
        ...formData.evaluationSteps,
        [stepKey]: checked
      }
    };
    setFormData(newData);
    onDataChange?.(newData);
  };

  // Validation function
  const validateForm = () => {
    const errors = {};
    
    // Role is always mandatory
    if (!formData.selectedRole) {
      errors.role = 'Role is required';
    }
    
    // Seniority is always mandatory (but it has a default value, so this should rarely trigger)
    if (!formData.seniority) {
      errors.seniority = 'Seniority level is required';
    }
    
    // Job is mandatory if Job tab is selected
    if (formData.evaluationType === 'Job' && !formData.selectedJob) {
      errors.job = 'Job selection is required';
    }
    
    // Talent is mandatory if Talent tab is selected
    if (formData.evaluationType === 'Talent' && !formData.selectedTalent) {
      errors.talent = 'Talent selection is required';
    }
    
    // Evaluation name is mandatory
    if (!formData.evaluation_name || formData.evaluation_name.trim() === '') {
      errors.evaluation_name = 'Evaluation name is required';
    }
    
    return errors;
  };

  // Function to trigger validation and show errors
  const triggerValidation = () => {
    const errors = validateForm();
    setValidationErrors(errors);
    setShowValidation(true);
    return Object.keys(errors).length === 0;
  };

  // Expose validation function to parent component
  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        ...formData,
        validateForm: triggerValidation
      });
    }
  }, [formData]);

  // Prefill role when job/talent is selected (similar to CreateEvaluation)
  useEffect(() => {
    if (formData.selectedTalent?.roleCategory) {
      const newData = {
        ...formData,
        selectedRole: {
          subCategory: formData.selectedTalent.roleCategory.subCategory,
          mainCategory: formData.selectedTalent.roleCategory.mainCategory,
          altSubcategory: formData.selectedTalent.roleCategory.altSubcategory
        }
      };
      setFormData(newData);
      onDataChange?.(newData);
    } else if (formData.selectedJob?.job_category_json) {
      const newData = {
        ...formData,
        selectedRole: {
          subCategory: formData.selectedJob.job_category_json.subCategory,
          mainCategory: formData.selectedJob.job_category_json.mainCategory,
          altSubcategory: formData.selectedJob.job_category_json.altSubcategory
        }
      };
      setFormData(newData);
      onDataChange?.(newData);
    }
  }, [formData.selectedTalent, formData.selectedJob]);

  return (
    <div className="grid h-full relative" style={{ gridTemplateColumns: '66.67% 33.33%' }}>
      {/* Left Column - Form with blue background, scrollable */}
      <div 
        className="overflow-y-auto" 
        style={{ 
          height: 'calc(100% - 88px)', // Account for navigation bar height
          backgroundColor: 'var(--color-brand-blue-light)',
          WebkitOverflowScrolling: 'touch', // Smooth iOS-style scrolling
          scrollBehavior: 'smooth' // Smooth scrolling behavior
        }}
      >
        <div className="p-8 space-y-6">
        {/* Centered Title */}
        <div className="text-center mb-8">
          <h2 className="text-4xl font-regular text-gray-900 mb-2 mt-4">Create Evaluation</h2>
          <p className="text-md text-gray-600 mb-8">
            {(() => {
              console.log(formData);
              if (formData.evaluationType === 'Job' && formData.selectedJob) {
                return `${formData.selectedJob.title} at ${formData.selectedJob.company}`;
              } else if (formData.evaluationType === 'Talent' && formData.selectedTalent) {
                return `${formData.selectedTalent.name}`;
              } else if (formData.selectedRole) {
                return `${formData.selectedRole.subCategory}`;
              } else {
                return "New Evaluation Setup";
              }
            })()}
          </p>
          <p className="text-2xl text-black-600 mt-6">
            What are you creating the evaluation for?
          </p>
        </div>
        
        {/* Evaluation Type Tabs */}
        <div className="max-w-2xl">
          
          {/* Unified Container with Tabs and Fields */}
          <div className="border-2 border-gray-200 rounded-lg bg-white overflow-visible">
            {/* Tab Header */}
            <div className="flex space-x-1 p-1">
              {[
                { value: 'Job', label: 'Job' },
                { value: 'Talent', label: 'Talent' },
                { value: 'Custom', label: 'Other' }
              ].map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => handleChange('evaluationType', tab.value)}
                  className={`flex-1 py-2 px-4 text-md font-medium rounded-md transition-colors ${
                    formData.evaluationType === tab.value
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            
            {/* Form Fields */}
            <div className="p-4 space-y-4">
              {/* Conditional Fields Based on Evaluation Type */}
              {formData.evaluationType === 'Job' && (
                <div>
                  <JobSearchSelect
                    selectedJob={formData.selectedJob}
                    onJobSelect={handleJobSelect}
                    onClear={clearJobSelection}
                    disabled={false}
                    clearable={true}
                  />
                  {showValidation && validationErrors.job && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.job}</p>
                  )}
                </div>
              )}

              {formData.evaluationType === 'Talent' && (
                <div>
                  <TalentSearchSelect
                    selectedTalent={formData.selectedTalent}
                    onTalentSelect={handleTalentSelect}
                    onClear={clearTalentSelection}
                    disabled={false}
                    clearable={true}
                  />
                  {showValidation && validationErrors.talent && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.talent}</p>
                  )}
                </div>
              )}

              {/* Role and Seniority - Show for all types */}
              <div className="grid gap-4" style={{gridTemplateColumns: '65% 32%'}}>
                <div>
                  <RoleSearchSelect
                    key={formData.evaluationType}
                    selectedRole={formData.selectedRole}
                    onRoleSelect={handleRoleSelect}
                    onClear={clearRoleSelection}
                  />
                  {showValidation && validationErrors.role && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.role}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="seniority" className="block text-base font-medium text-gray-700 mb-2">
                    Seniority Level
                  </label>
                  <select
                    id="seniority"
                    name="seniority"
                    value={formData.seniority}
                    onChange={handleSeniorityChange}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Junior">Junior</option>
                    <option value="Middle">Middle</option>
                    <option value="Senior">Senior</option>
                  </select>
                  {showValidation && validationErrors.seniority && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.seniority}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Evaluation Name Section - Moved to left side */}
        <div className="mb-6">
          <label className="block text-base font-medium text-gray-700 mb-2">
            Evaluation Name (internal only)
          </label>
          <input
            type="text"
            value={formData.evaluation_name}
            onChange={(e) => handleChange('evaluation_name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter evaluation name"
          />
          {showValidation && validationErrors.evaluation_name && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.evaluation_name}</p>
          )}
        </div>

        {/* Default Evaluation Checkbox - Show only for Job evaluations */}
        {formData.evaluationType === 'Job' && (
          <div className="mb-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_default_evaluation"
                checked={formData.is_default_evaluation || false}
                onChange={(e) => handleChange('is_default_evaluation', e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              <label htmlFor="is_default_evaluation" className="ml-3 text-base font-medium text-gray-700">
                Default Evaluation
              </label>
            </div>
          </div>
        )}

        {/* Expiry Countdown Enabled Checkbox */}
        <div className="mb-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="countdown_enabled"
              checked={formData.countdown_enabled || false}
              onChange={(e) => handleChange('countdown_enabled', e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
            <label htmlFor="countdown_enabled" className="ml-3 text-base font-medium text-gray-700">
              Expiry Countdown Enabled
            </label>
          </div>
        </div>

        {/* Evaluation Steps Section */}
        <div className="mb-6">
          <h3 className="text-base font-medium text-gray-700 mb-3">Evaluation Steps</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-center opacity-50 pointer-events-none">
              <input
                type="checkbox"
                id="resume"
                checked={formData.evaluationSteps.resume}
                disabled
                className="w-4 h-4 text-gray-400 bg-gray-100 border-2 border-gray-300 rounded mr-3 cursor-not-allowed"
              />
              <label htmlFor="resume" className="text-sm text-gray-400 cursor-not-allowed">
                Ask for Resume
              </label>
            </div>
            <div className="flex items-center opacity-50 pointer-events-none">
              <input
                type="checkbox"
                id="kyc"
                checked={formData.evaluationSteps.kyc}
                disabled
                className="w-4 h-4 text-gray-400 bg-gray-100 border-2 border-gray-300 rounded mr-3 cursor-not-allowed"
              />
              <label htmlFor="kyc" className="text-sm text-gray-400 cursor-not-allowed">
                Identify Verification (KYC)
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="video_intro"
                checked={formData.evaluationSteps.video_intro}
                onChange={(e) => handleStepChange('video_intro', e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-white border-2 border-gray-300 rounded mr-3 focus:ring-blue-500 focus:ring-2"
              />
              <label htmlFor="video_intro" className="text-sm text-gray-700">
                Video Intro
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="questionnaire"
                checked={formData.evaluationSteps.questionnaire}
                onChange={(e) => handleStepChange('questionnaire', e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-white border-2 border-gray-300 rounded mr-3 focus:ring-blue-500 focus:ring-2"
              />
              <label htmlFor="questionnaire" className="text-sm text-gray-700">
                Questionnaire
              </label>
            </div>
            <div className="flex items-center opacity-50 pointer-events-none">
              <input
                type="checkbox"
                id="challenge"
                checked={formData.evaluationSteps.challenge}
                disabled
                className="w-4 h-4 text-gray-400 bg-gray-100 border-2 border-gray-300 rounded mr-3 cursor-not-allowed"
              />
              <label htmlFor="challenge" className="text-sm text-gray-400 cursor-not-allowed">
                Challenge
              </label>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Right Column - Explainer content with vertical center alignment */}
      <div className="bg-white flex items-center justify-center p-8">
        <div className="text-center max-w-sm">
          {/* Centered Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
              <span className="text-white text-lg font-bold">i</span>
            </div>
          </div>
          
          {/* Main Heading */}
          <h3 className="text-xl font-semibold text-gray-900 mb-6 leading-tight">
            Select the Job, Talent or Custom Role for which you're creating the evaluation.
          </h3>
          
          {/* Subtext */}
          <p className="text-base text-gray-600 leading-relaxed">
            Further configure the evaluation by selecting the steps it should include, and give it a name.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Step_Start;
